import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import main_screenshot from "../assets/images/main_screenshot.png";
import "./CRHome.css";

function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setInView(true);
      },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function useDeviceTheme() {
  const [isDark, setIsDark] = useState(
    window.matchMedia("(prefers-color-scheme: dark)").matches,
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    // Lytter efter hvis brugeren skifter tema i deres systemindstillinger
    const handleChange = (e) => setIsDark(e.matches);
    mediaQuery.addEventListener("change", handleChange);

    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  return isDark;
}
const SERVICES = [
  {
    num: "01",
    title: "Custom Web Development",
    desc: "Websites and web applications built in HTML, React, and React Native — from concept to finished product.",
    tags: ["HTML / CSS", "React", "React Native"],
  },
  {
    num: "02",
    title: "Freelance & Consulting",
    desc: "Need an extra hand for your project? I step in as a freelancer and deliver quality code efficiently.",
    tags: ["Freelance", "Code Review", "Sparring"],
  },
  {
    num: "03",
    title: "Roster Connect",
    desc: "My own mobile app that transforms PDF shift schedules into an intuitive overview with calendar export.",
    tags: ["Mobile App", "Subscription"],
    link: "/roster-connect",
  },
  {
    num: "04",
    title: "API & Backend",
    desc: "I develop and maintain custom API solutions — stable, secure, and scalable.",
    tags: ["REST API", "DigitalOcean", "Hosting"],
  },
];

function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const isDarkMode = useDeviceTheme();

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }, 80);
  };

  return (
    <nav className={`cr-nav ${scrolled ? "scrolled" : ""}`}>
      <div className="cr-nav-inner">
        <div className="cr-logo">
          <span className="cr-logo-mark">CR</span>
          <span className="cr-logo-name">Software</span>
          <span className="cr-logo-aps">ApS</span>
        </div>
        <ul className="cr-nav-links">
          {["Services", "Projects", "About", "Contact"].map((l) => (
            <li key={l}>
              <button onClick={() => go(l.toLowerCase().replace(" ", "-"))}>
                {l}
              </button>
            </li>
          ))}
        </ul>
        <button className="cr-nav-cta" onClick={() => go("contact")}>
          Contact me
        </button>
        <button
          className="cr-burger"
          onClick={() => setOpen(!open)}
          aria-label="menu"
        >
          <span />
          <span />
        </button>
      </div>
      {open && (
        <div className="cr-mobile-menu">
          {["Services", "Projects", "About", "Contact"].map((l) => (
            <button
              key={l}
              onClick={() => go(l.toLowerCase().replace(" ", "-"))}
            >
              {l}
            </button>
          ))}
        </div>
      )}
    </nav>
  );
}

function Hero() {
  return (
    <section className="cr-hero">
      <div className="cr-hero-bg">
        <div className="cr-grid" />
        <div className="cr-glow g1" />
        <div className="cr-glow g2" />
      </div>
      <div className="cr-hero-inner">
        <div className="cr-eyebrow">
          <span className="cr-dot" />
          One man. Many solutions.
        </div>
        <h1>
          Software that
          <br />
          <span className="cr-h1-accent">makes a difference</span>
        </h1>
        <p className="cr-hero-sub">
          CR Software ApS is run by an independent developer building web
          applications, offering freelance services, and operating Roster
          Connect — with a straightforward focus on quality code and simple
          solutions.
        </p>
        <div className="cr-hero-actions">
          <button
            className="cr-btn-primary"
            onClick={() =>
              document
                .getElementById("contact")
                ?.scrollIntoView({ behavior: "smooth" })
            }
          >
            Let's talk
          </button>
          <Link to="/roster-connect" className="cr-btn-ghost">
            View Roster Connect →
          </Link>
        </div>
        <div className="cr-hero-chips">
          <span>HTML</span>
          <span>React</span>
          <span>React Native</span>
          <span>REST API</span>
        </div>
        <div className="cr-terminal-wrap">
          <div className="cr-terminal">
            <div className="cr-terminal-bar">
              <span />
              <span />
              <span />
            </div>
            <div className="cr-terminal-body">
              <p>
                <em className="t-gr">$</em> <span className="t-pu">whoami</span>
              </p>
              <p className="t-wh">CR Software ApS</p>
              <p>
                <em className="t-gr">$</em>{" "}
                <span className="t-pu">ls services/</span>
              </p>
              <p className="t-cy">web-development/</p>
              <p className="t-cy">freelance/</p>
              <p className="t-cy">roster-connect/</p>
              <p className="t-cy">api-hosting/</p>
              <p>
                <em className="t-gr">$</em> <span className="t-blink">_</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Services() {
  const [ref, inView] = useInView();
  return (
    <section className="cr-section cr-services" id="services" ref={ref}>
      <div className="cr-section-label">What I do</div>
      <h2 className="cr-h2">Services</h2>
      <div className={`cr-services-grid ${inView ? "in" : ""}`}>
        {SERVICES.map((s, i) => (
          <div
            className={`cr-service-card ${s.link ? "has-link" : ""}`}
            key={s.num}
            style={{ "--d": `${i * 0.09}s` }}
          >
            <div className="cr-svc-num">{s.num}</div>
            <h3>{s.title}</h3>
            <p>{s.desc}</p>
            <div className="cr-tags">
              {s.tags.map((t) => (
                <span key={t}>{t}</span>
              ))}
            </div>
            {s.link && (
              <Link to={s.link} className="cr-svc-link">
                View the app →
              </Link>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function Projects() {
  const [ref, inView] = useInView();
  return (
    <section className="cr-section cr-projects" id="projects" ref={ref}>
      <div className="cr-section-label">Featured Project</div>
      <h2 className="cr-h2">Roster Connect</h2>
      <div className={`cr-project-card ${inView ? "in" : ""}`}>
        <div className="cr-project-text">
          <div className="rc-badge">Mobile APP · Subscription</div>
          <h3>Schedules made simple</h3>
          <p>
            Roster Connect is my own application that automatically decodes PDF
            roster schedules and presents them in a clean, intuitive design.
            Users can export directly to their calendar and manage their shifts
            effortlessly.
          </p>
          <p>
            Built as a subscription-based product with a dedicated API hosted on
            DigitalOcean.
          </p>
          <Link to="/roster-connect" className="cr-btn-primary cr-project-cta">
            Go to Roster Connect
          </Link>
        </div>
        <div className="cr-project-visual">
          <div className="rc-hero-phone">
            <div className="rc-phone-frame">
              <div className="rc-phone-notch" />
              <img
                src={main_screenshot}
                style={{ borderRadius: 30 }}
                alt="Logo"
              ></img>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function About() {
  const [ref, inView] = useInView();
  return (
    <section className="cr-section cr-about" id="about" ref={ref}>
      <div className={`cr-about-inner ${inView ? "in" : ""}`}>
        <div className="cr-about-text">
          <div className="cr-section-label">About me</div>
          <h2 className="cr-h2">
            One person,
            <br />
            full dedication
          </h2>
          <p>
            I am an independent software developer running CR Software ApS.
            Daily, I work on Roster Connect, but I also take on freelance
            projects within HTML, React, and React Native.
          </p>
          <p>
            You always deal directly with me — no middlemen, no
            misunderstandings. I am accustomed to working independently and
            delivering on time.
          </p>
          <div className="cr-about-stats">
            <div>
              <span>React</span>
              <span>& React Native</span>
            </div>
            <div>
              <span>HTML / CSS</span>
              <span>Frontend</span>
            </div>
            <div>
              <span>API</span>
              <span>Backend</span>
            </div>
          </div>
        </div>
        <div className="cr-about-code">
          <div className="cr-code-block">
            <span className="t-gr">// cr-software.js</span>
            <br />
            <span className="t-pu">const</span>{" "}
            <span className="t-cy">dev</span> = {"{"}
            <br />
            &nbsp;&nbsp;name: <span className="t-ye">"CR Software ApS"</span>,
            <br />
            &nbsp;&nbsp;developer:{" "}
            <span className="t-ye">"Christian Rosenbaek"</span>,<br />
            &nbsp;&nbsp;stack: [<span className="t-ye">"React"</span>,{" "}
            <span className="t-ye">"RN"</span>,{" "}
            <span className="t-ye">"HTML"</span>],
            <br />
            &nbsp;&nbsp;product: <span className="t-ye">"Roster Connect"</span>,
            <br />
            &nbsp;&nbsp;available: <span className="t-or">true</span>,<br />
            {"}"};
          </div>
        </div>
      </div>
    </section>
  );
}

function Contact() {
  const [form, setForm] = useState({ name: "", email: "", msg: "" });
  const [sent, setSent] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    setSent(true);
  };

  return (
    <section className="cr-section cr-contact" id="contact">
      <div className="cr-section-label">Contact</div>
      <h2 className="cr-h2">Let's talk</h2>
      <div className="cr-contact-inner">
        <div className="cr-contact-info">
          <p>
            Have a project you need help with, or are you curious about Roster
            Connect? Send me a message — I'll get back to you ASAP.
          </p>
        </div>
        <div className="cr-contact-items">
          <div className="cr-ci">
            <span>Email</span>
            <a href="mailto:contact@crsoftware.dk">contact@crsoftware.dk</a>
          </div>
          <div className="cr-ci">
            <span>VAT / CVR</span>
            <a>44526956</a>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="cr-footer">
      <div className="cr-footer-inner">
        <div className="cr-logo">
          <span className="cr-logo-mark">CR</span>
          <span className="cr-logo-name">Software</span>
          <span className="cr-logo-aps">ApS</span>
        </div>
        <div className="cr-footer-links">
          <Link to="/roster-connect">Roster Connect</Link>
          <Link to="/roster-connect/privacy">Privacy Policy</Link>
          <Link to="/roster-connect/terms">Terms of Service</Link>
        </div>
        <p className="cr-footer-copy">
          © {new Date().getFullYear()} CR Software ApS
        </p>
      </div>
    </footer>
  );
}

export default function CRHome() {
  return (
    <div className="cr-root">
      <Navbar />
      <Hero />
      <Services />
      <Projects />
      <About />
      <Contact />
      <Footer />
    </div>
  );
}
